import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "./index.css";

// Redux Imports
import { internalTransactionsViewAll } from "../../../redux/features/transactions/internalTransactions";
import { beneficiaryTransactionsViewAll } from "../../../redux/features/transactions/beneficiaryTransactions";

// Components Imports
import ICONS from "../../../assets/Icons";
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import useCategories from "../../../utils/useCategories";
import IconRoundButton from "../../_utils/CommonButtons/IconRoundButton";
import PrimaryButton from "../../_utils/CommonButtons/PrimaryButton";
import TopBarButtons from "../../_utils/CommonButtons/TopBarButton";
import CommonCalendar from "../../_utils/CommonCalendar";
import SingleSelectDropDownComponent from "../../_utils/CommonDropdown/SingleSelectDropdown";
import TextInputField from "../../_utils/CommonInputFields/TextInputField";
import TitleSubtitlePair from "../../_utils/TitleSubtitlePair";
import MultiSelectDropDownComponent from "../../_utils/CommonDropdown/MultiSelectDropdown";
import Loader from "../../Loader/Index";
import CommonToast from "../../_utils/CommonToast";
import Validation from "../../../utils/validation";
import TEXT from "../../../utils/globalText";

const MISReportCreateFilter = (props) => {
  useCategories();
  const dispatch = useDispatch();

  const { categoriesData } = useSelector((state) => state.sharedResources);

  const [columnstate, setColumnState] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [isLabelsLoading, setIsLabelsLoading] = useState(false);

  const filterCardRef = useRef(null);

  // CONSTANTS & CONFIGURATIONS
  const DATE_FIELDS = [
    TEXT.ACCOUNT_STATEMENT_ENTRY_DATE,
    TEXT.APPROVED_ON,
    TEXT.CREATED_ON,
    TEXT.DISTRIBUTION_COMPLETION_DATE,
    TEXT.REJECTED_ON,
  ];

  const TEXT_FIELDS = [
    TEXT.ENTITY_ID,
    TEXT.APPROVED_BY,
    TEXT.BENEFICIARY_ACCOUNT_BANK_NAME,
    TEXT.BENEFICIARY_ACCOUNT_IFSC,
    TEXT.BENEFICIARY_ACCOUNT_NAME,
    TEXT.BENEFICIARY_ACCOUNT_NUMBER,
    TEXT.OWNER_ACCOUNT_BANK_NAME,
    TEXT.OWNER_ACCOUNT_IFSC,
    TEXT.OWNER_ACCOUNT_NAME,
    TEXT.BENEFICIARY_ACCOUNT_PAN,
    TEXT.REJECTED_BY,
    TEXT.REJECT_REASON_LABEL,
    TEXT.TRANSACTION_INITIATOR,
    TEXT.USERNAME,
    TEXT.ACCOUNT_ID,
    TEXT.DISTRIBUTION_REMARK,
    TEXT.ADDITIONAL_RECIPIENT,
    TEXT.BENEFICIARY_ACCOUNT_EMAIL_ID,
    TEXT.ACCOUNT_NAME,
  ];

  const NUMBER_FIELDS = [
    TEXT.AMOUNT,
    TEXT.BENEFICIARY_ACCOUNT_CREATED_COUNT,
    TEXT.BENEFICIARY_TRANSACTION_CREATED_COUNT,
    TEXT.BENEFICIARY_TRANSACTION_APPROVED_COUNT,
    TEXT.INTERNAL_TRANSACTION_CREATED_COUNT,
    TEXT.INTERNAL_TRANSACTION_APPROVED_COUNT,
    TEXT.OPENING_BALANCE,
    TEXT.CLOSING_BALANCE,
    TEXT.ACCOUNT_NUMBER,
    TEXT.OWNER_ACCOUNT_NUMBER,
  ];

  const HIDDEN_FIELDS = [TEXT.CONFIDENTIAL_TRANSACTION, TEXT.CREDIT_DEBIT];

  const DROPDOWN_OPTIONS = {
    [TEXT.TRANSACTION_APPROVAL_STATUS]: [
      TEXT.APPROVED.toLowerCase(),
      TEXT.PENDING.toLowerCase(),
      TEXT.REJECTED.toLowerCase(),
      TEXT.ABANDONED.toLowerCase(),
      TEXT.BLOCKED.toLowerCase(),
    ],

    [TEXT.ACCOUNT_STATUS]: [
      TEXT.APPROVED.toLowerCase(),
      TEXT.PENDING.toLowerCase(),
      TEXT.REJECTED.toLowerCase(),
      TEXT.BLOCKED.toLowerCase(),
      TEXT.ARCHIVED.toLowerCase(),
    ],

    [TEXT.TRANSACTION_DISTRIBUTION_STATUS]: [
      TEXT.DISTRIBUTION_COMPLETE.toLowerCase(),
      TEXT.DISTRIBUTION_CREATED.toLowerCase(),
      TEXT.DISTRIBUTION_APPROVED.toLowerCase(),
      TEXT.AWAITING_FUNDS.toLowerCase(),
      TEXT.DISTRIBUTION_INITIATED.toLowerCase(),
      TEXT.DISTRIBUTION_PENDING.toLowerCase(),
      TEXT.DISTRIBUTION_REJECTED.toLowerCase(),
      TEXT.DISTRIBUTION_FAILED.toLowerCase(),
      TEXT.DISTRIBUTION_REVERSAL.toLowerCase(),
      TEXT.DISTRIBUTION_DENIED.toLowerCase(),
      TEXT.DISTRIBUTION_ABANDONED.toLowerCase(),
    ],

    [TEXT.PAYMENT_METHOD]: [TEXT.NEFT, TEXT.RTGS],

    [TEXT.OWNER_ACCOUNT_TYPE]: [
      TEXT.CURRENT_ACCOUNT,
      TEXT.OVERDRAFT_ACCOUNT,
      TEXT.CASH_CREDIT_ACCOUNT,
      TEXT.FIXED_DEPOSIT_ACCOUNT,
    ],
  };

  const NOTE_DETAILS_EXCLUDED = [
    TEXT.AMOUNT,
    TEXT.BENEFICIARY_ACCOUNT_CREATED_COUNT,
    TEXT.BENEFICIARY_TRANSACTION_CREATED_COUNT,
    TEXT.BENEFICIARY_TRANSACTION_APPROVED_COUNT,
    TEXT.INTERNAL_TRANSACTION_CREATED_COUNT,
    TEXT.INTERNAL_TRANSACTION_APPROVED_COUNT,
    TEXT.BENEFICIARY_ACCOUNT_PAN,
    TEXT.BENEFICIARY_ACCOUNT_IFSC,
    TEXT.OWNER_ACCOUNT_IFSC,
    TEXT.OPENING_BALANCE,
    TEXT.CLOSING_BALANCE,
    TEXT.BENEFICIARY_ACCOUNT_EMAIL_ID,
    TEXT.ADDITIONAL_RECIPIENT,
  ];

  const SIMILAR_COLUMNS = [
    TEXT.APPROVED_BY,
    TEXT.BENEFICIARY_ACCOUNT_BANK_NAME,
    TEXT.BENEFICIARY_ACCOUNT_IFSC,
    TEXT.BENEFICIARY_ACCOUNT_NAME,
    TEXT.OWNER_ACCOUNT_BANK_NAME,
    TEXT.OWNER_ACCOUNT_IFSC,
    TEXT.OWNER_ACCOUNT_NAME,
    TEXT.BENEFICIARY_ACCOUNT_PAN,
    TEXT.REJECTED_BY,
    TEXT.TRANSACTION_INITIATOR,
    TEXT.USERNAME,
    TEXT.ACCOUNT_NAME,
  ];

  const EXCLUDED_COLUMNS = [
    TEXT.TRANSACTION_ID,
    TEXT.ENTITY_ID,
    TEXT.ACCOUNT_ID,
    TEXT.NARRATION,
    TEXT.DISTRIBUTION_REMARK,
    TEXT.REJECT_REASON_LABEL,
    TEXT.OWNER_ACCOUNT_BALANCE,
  ];

  const selectedColumns = props?.columnsData
    ?.filter(
      (col) => col.selected && !EXCLUDED_COLUMNS.includes(col.columnName),
    )
    ?.map((itm) => itm.columnName);

  // UTILITY FUNCTIONS
  const isValidEmail = (email) => {
    const regex = Validation?.emailRegex;
    return regex.test(email);
  };

  const isValidPanNumber = (pan) => {
    const panNoRegex = Validation?.panRegex;
    return panNoRegex.test(pan);
  };

  const isValidIFSCCode = (ifsc) => {
    const ifscCodeRegex = Validation?.ifscRegex;
    return ifscCodeRegex.test(ifsc);
  };

  const isValidAccountNumber = (accNo) => {
    return typeof accNo === "string" && accNo.length >= 9;
  };

  const isValidMinLength = (val) => {
    return typeof val === "string" && val.trim().length >= 3;
  };

  const showToast = (message) => {
    CommonToast({
      text: message,
      icon: ICONS?.LOADER_GIF,
      variant: "var(--processing)",
      buttonNeeded: false,
      btnProps: {
        enabledBgColor: "var(--primary-bg)",
        enabledTextColor: "var(--text-primary)",
        btnText: "",
        handleFunction: () => {},
        icon: "",
        btnType: "button",
        isDisabled: false,
        hoverOption: "white",
      },
    });
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  const parseYYYYMMDDToDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split("-");
    return new Date(day, month - 1, year);
  };

  const handleDateChange2 = (date, currentIndex) => {
    if (date) {
      const dateInYYYYMMDD = formatDateToYYYYMMDD(date);

      const updatedData = props?.filterMapping.map((itm, idx) => {
        if (idx === currentIndex) {
          return {
            ...itm,
            input_value: dateInYYYYMMDD,
            active: false,
          };
        }

        return itm;
      });

      props?.setFilterMapping(updatedData);
      props?.setActiveTab(null);
      setColumnState("");
      setIsUserTyping(false);
    } else {
      const updatedData = props?.filterMapping.map((itm, idx) => {
        if (idx === currentIndex) {
          return {
            ...itm,
            input_value: "",
            active: true,
          };
        }

        return itm;
      });

      props?.setFilterMapping(updatedData);
    }
  };

  const allowedNameInput = (value, columnName) => {
    if (!value) return true;

    if (columnName === TEXT.BENEFICIARY_ACCOUNT_PAN) {
      return value.length <= GlobalConfiguration?.panLength;
    }

    if (
      columnName === TEXT.BENEFICIARY_ACCOUNT_IFSC ||
      columnName === TEXT.OWNER_ACCOUNT_IFSC
    ) {
      return value.length <= GlobalConfiguration?.ifscLength;
    }

    return value.length <= GlobalConfiguration?.inputValidationLength;
  };

  const handleInput = (e) => {
    let { value } = e.target;
    const currentFilter = props?.filterMapping[props?.activeTab];
    const columnName = currentFilter?.column_name;

    if (allowedNameInput(value, columnName)) {
      setIsUserTyping(true);

      const formattedValue =
        columnName === TEXT.BENEFICIARY_ACCOUNT_PAN ||
        columnName === TEXT.BENEFICIARY_ACCOUNT_IFSC ||
        columnName === TEXT.OWNER_ACCOUNT_IFSC
          ? value?.toUpperCase()
          : value;

      let data = props?.filterMapping.map((itm, idx) => {
        if (idx === props?.activeTab) {
          return {
            ...itm,
            input_value: formattedValue,
          };
        } else {
          return itm;
        }
      });
      props?.setFilterMapping(data);
    }
  };

  const handleAmount = (value) => {
    setIsUserTyping(true);

    const currentColumn = props?.filterMapping[props?.activeTab]?.column_name;
    const isAccountNumberField =
      currentColumn === TEXT.ACCOUNT_NUMBER ||
      currentColumn === TEXT.OWNER_ACCOUNT_NUMBER;

    if (isAccountNumberField) {
      const sanitizedDigits = value.replace(/\D/g, "");

      if (sanitizedDigits.length <= GlobalConfiguration?.categorymaxLength) {
        let data = props?.filterMapping.map((itm, idx) => {
          if (idx === props?.activeTab) {
            return {
              ...itm,
              input_value: sanitizedDigits,
            };
          } else {
            return itm;
          }
        });
        props?.setFilterMapping(data);
      }
      return;
    }

    if (
      value[0] !== GlobalConfiguration?.lengthCheckZero ||
      props?.selectedOption === TEXT.USER
    ) {
      const sanitizedText = value
        .replace(/[^0-9.]/g, "")
        .replace(/(\..*)\./g, "$1");
      const amount = parseFloat(sanitizedText);

      if (sanitizedText.length !== "" && !isNaN(amount)) {
        if (
          props?.selectedOption === TEXT.USER &&
          value[0] === "0" &&
          value.length > GlobalConfiguration?.lengthCheckOne
        )
          return;
        if (value.includes(".") && value.length > 12) return;
        if (!value.includes(".") && value.length > 9) return;
        const regex = /^\d*\.?\d{0,2}$/;

        if (regex.test(value)) {
          let data = props?.filterMapping.map((itm, idx) => {
            if (idx === props?.activeTab) {
              return {
                ...itm,
                input_value: value?.toUpperCase(),
                // active: false,
              };
            } else {
              return itm;
            }
          });
          props?.setFilterMapping(data);
        }
      } else {
        let data = props?.filterMapping.map((itm, idx) => {
          if (idx === props?.activeTab) {
            return {
              ...itm,
              input_value: "",
              //  active: false,
            };
          } else {
            return itm;
          }
        });
        props?.setFilterMapping(data);
      }
    }
  };

  const fetchLabelsData = async () => {
    if (
      ![TEXT.BENEFICIARY_TRANSACTION, TEXT.INTERNAL_TRANSACTION].includes(
        props?.selectedOption,
      ) ||
      props?.labelsOptions.length > GlobalConfiguration?.lengthCheckZero
    ) {
      return;
    }

    try {
      setIsLabelsLoading(true);
      showToast(TEXT?.FETCHING_LABELS);
      const queryParams = {
        page_no: 1,
        page_item_count: 50,
      };

      const actionToDispatch =
        props?.selectedOption === TEXT.INTERNAL_TRANSACTION
          ? internalTransactionsViewAll(queryParams)
          : beneficiaryTransactionsViewAll(queryParams);

      const response = await dispatch(actionToDispatch);

      if (response?.meta?.requestStatus === "fulfilled") {
        const rawData = response?.payload?.summary?.available_labels || [];
        props?.setLabelsOptions(rawData);

        toast.dismiss();
        CommonToast({
          text: TEXT?.LABELS_FETCHED_SUCCESSFULLY,
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
          variant: "var(--success)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      }
    } catch (error) {
      let errorMessage =
        error?.response?.data ||
        error?.message ||
        "Unable to fetch labels. Try again later.";
      toast.dismiss();
      CommonToast({
        text: errorMessage,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });

      if (props?.activeTab !== null && props?.filterMapping) {
        const resetData = props?.filterMapping.map((itm, idx) => {
          if (idx === props?.activeTab) {
            return {
              ...itm,
              column_name: "",
              criteria_name: "",
              input_value: "",
              active: true,
            };
          }
          return itm;
        });
        props?.setFilterMapping(resetData);
      }
      setColumnState("");
    } finally {
      setIsLabelsLoading(false);
    }
  };

  const renderTextInput = (filter, currentIndex, onInput = handleInput) => {
    const isCardInactive = props?.activeTab !== currentIndex && !filter?.active;

    const isEmailField =
      filter?.column_name === TEXT.ADDITIONAL_RECIPIENT ||
      filter?.column_name === TEXT.BENEFICIARY_ACCOUNT_EMAIL_ID;

    const isPanField = filter?.column_name === TEXT.BENEFICIARY_ACCOUNT_PAN;

    const isIfscField =
      filter?.column_name === TEXT.BENEFICIARY_ACCOUNT_IFSC ||
      filter?.column_name === TEXT.OWNER_ACCOUNT_IFSC;

    const isAccountNumberField =
      filter?.column_name === TEXT.BENEFICIARY_ACCOUNT_NUMBER ||
      filter?.column_name === TEXT.OWNER_ACCOUNT_NUMBER ||
      filter?.column_name === TEXT.ACCOUNT_NUMBER;

    const isMin3CharRequired =
      !NOTE_DETAILS_EXCLUDED.includes(filter?.column_name) &&
      !isEmailField &&
      !isPanField &&
      !isIfscField &&
      !isAccountNumberField;

    const isInvalidEmail =
      isEmailField && filter?.input_value && !isValidEmail(filter.input_value);

    const isInvalidPan =
      isPanField &&
      filter?.input_value &&
      !isValidPanNumber(filter.input_value);

    const isInvalidIfsc =
      isIfscField &&
      filter?.input_value &&
      !isValidIFSCCode(filter.input_value);

    const isInvalidAccountNumber =
      isAccountNumberField &&
      filter?.input_value &&
      !isValidAccountNumber(filter.input_value);

    const isInvalidMin3Char =
      isMin3CharRequired &&
      filter?.input_value &&
      !isValidMinLength(filter.input_value);

    const hasValidationError =
      isInvalidEmail ||
      isInvalidPan ||
      isInvalidIfsc ||
      isInvalidAccountNumber ||
      isInvalidMin3Char;

    const getPlaceholder = () => {
      const col = filter?.column_name;

      if (isEmailField) return TEXT.EMAIL_ID;
      if (isPanField) return TEXT.PAN_NUMBER;
      if (isIfscField) return TEXT.IFSC_CODE;
      if (isAccountNumberField) return TEXT.ACCOUNT_NUMBER;

      switch (col) {
        case TEXT.TRANSACTION_INITIATOR:
        case TEXT.AMOUNT:
        case TEXT.APPROVED_BY:
        case TEXT.USERNAME:
        case TEXT.REJECTED_BY:
          return col;

        case TEXT.BENEFICIARY_ACCOUNT_NAME:
        case TEXT.ACCOUNT_NAME:
          return TEXT.ACCOUNT_NAME;

        case TEXT.BENEFICIARY_ACCOUNT_BANK_NAME:
        case TEXT.OWNER_ACCOUNT_BANK_NAME:
          return TEXT.BANK_NAME;

        case TEXT.BENEFICIARY_ACCOUNT_CREATED_COUNT:
          return "Number of accounts created";

        case TEXT.BENEFICIARY_TRANSACTION_CREATED_COUNT:
        case TEXT.INTERNAL_TRANSACTION_CREATED_COUNT:
        case TEXT.BENEFICIARY_TRANSACTION_APPROVED_COUNT:
        case TEXT.INTERNAL_TRANSACTION_APPROVED_COUNT:
          return "Number of transactions created";

        case TEXT.OPENING_BALANCE:
        case TEXT.CLOSING_BALANCE:
          return TEXT.BALANCE;

        default:
          return TEXT.DETAIL;
      }
    };

    const handleBlur = () => {
      if (hasValidationError) {
        toast.dismiss();
        let errorText = "";

        if (isInvalidEmail) errorText = "Invalid email format.";
        if (isInvalidPan) errorText = "Invalid PAN format.";
        if (isInvalidIfsc) errorText = "Invalid IFSC format.";

        CommonToast({
          text: errorText,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      }
    };

    return (
      <div
        className="Mis_report_filter_inputUser gap-y-gap-xs"
        style={{
          opacity: isCardInactive
            ? "var(--light-opacity)"
            : "var(--base-opacity)",
        }}
      >
        <div className="h-[--icon-sm]"></div>
        <TextInputField
          bgColor="var(--primary-card)"
          fgColor="var(--text-secondary)"
          placeholder={getPlaceholder()}
          variant={hasValidationError ? "error" : "default"}
          fontKind="small"
          value={filter?.input_value || ""}
          onInput={onInput}
          onBlur={handleBlur}
          isDisabled={isCardInactive}
          adornment={
            hasValidationError ? ICONS.ERROR_WITH_WHITE_OUTLINED_CIRCLE : ""
          }
        />
        {!NOTE_DETAILS_EXCLUDED.includes(filter?.column_name) && (
          <div className="text-text-secondary text-font-xs font-normal">
            {isAccountNumberField
              ? TEXT?.MINIMUM_9_CHARACTERS_REQUIRED
              : TEXT?.MINIMUM_3_CHARACTERS_REQUIRED}
          </div>
        )}
      </div>
    );
  };

  const renderField = (filter = null, currentIndex = null) => {
    const data = categoriesData?.categories?.map((itm) => itm.name) || [];

    const currentColumn = filter?.column_name || "";

    if (!currentColumn) return null;

    if (HIDDEN_FIELDS.includes(currentColumn)) {
      return null;
    }

    if (DATE_FIELDS.includes(currentColumn)) {
      const selectedCalendarDate = filter?.input_value
        ? parseYYYYMMDDToDate(filter.input_value)
        : null;

      const isCardInactive =
        props?.activeTab !== currentIndex && !filter?.active;
      return (
        <div
          className="w-full flex flex-col justify-center items-start gap-y-gap-xs"
          style={{
            opacity: isCardInactive
              ? "var(--light-opacity)"
              : "var(--base-opacity)",

            pointerEvents: isCardInactive ? "none" : "auto",
          }}
        >
          <p className="text-font-xs text-text-secondary text-left">
            {TEXT.DATE}
          </p>

          <CommonCalendar
            startDate={selectedCalendarDate}
            handleDateChange={(date) => handleDateChange2(date, currentIndex)}
            type="small"
            selectionType="single"
          />
        </div>
      );
    }

    if (TEXT_FIELDS.includes(currentColumn)) {
      return renderTextInput(filter, currentIndex);
    }

    if (NUMBER_FIELDS.includes(currentColumn)) {
      return renderTextInput(filter, currentIndex, (e) =>
        handleAmount(e.target.value),
      );
    }

    if (currentColumn === TEXT.LABELS) {
      const selectedLabels = Array.isArray(filter?.input_value)
        ? filter?.input_value
        : filter?.input_value
          ? [filter.input_value]
          : [];

      return (
        <div className="w-full Qsmd:w-dropdown-width flex flex-col justify-center items-start gap-y-gap-xs h-fit">
          <p className="text-font-xs text-text-secondary text-left">
            {TEXT.LABELS}
          </p>

          <MultiSelectDropDownComponent
            options={props?.labelsOptions}
            selected={selectedLabels}
            setSelected={(newSelected) => {
              const updatedData = props?.filterMapping.map((itm, idx) =>
                idx === currentIndex
                  ? {
                      ...itm,
                      input_value: newSelected,
                    }
                  : itm,
              );

              props?.setFilterMapping(updatedData);
            }}
            showSearch={true}
            placeholder={TEXT.SELECT_LABELS}
            searchPlaceholder={TEXT.SEARCH_LABEL}
            disabled={props?.activeTab !== currentIndex || isLabelsLoading}
            type="small"
          />
        </div>
      );
    }

    const options =
      currentColumn === TEXT.CATEGORY
        ? data
        : DROPDOWN_OPTIONS[currentColumn] || ["Include", "Exclude"];

    return (
      <div className="w-full Qsmd:w-dropdown-width flex flex-col justify-center items-start gap-y-gap-xs h-fit">
        <p className="text-font-xs text-text-secondary text-left">
          {currentColumn === TEXT.TRANSACTION_APPROVAL_STATUS ||
          currentColumn === TEXT.TRANSACTION_DISTRIBUTION_STATUS ||
          currentColumn === TEXT.ACCOUNT_STATUS
            ? TEXT.STATUS
            : currentColumn === TEXT.OWNER_ACCOUNT_TYPE
              ? TEXT.ACCOUNT_TYPE
              : currentColumn === TEXT.PAYMENT_METHOD
                ? TEXT.PAYMENT_METHOD
                : currentColumn === TEXT.CATEGORY
                  ? TEXT.CATEGORY
                  : ""}
        </p>

        <SingleSelectDropDownComponent
          options={options}
          selected={filter?.input_value || ""}
          setSelected={(value) => {
            const updatedData = props?.filterMapping.map((itm, idx) =>
              idx === props?.activeTab
                ? {
                    ...itm,
                    input_value: value,
                  }
                : itm,
            );

            props?.setFilterMapping(updatedData);
          }}
          type="small"
          showSearch={currentColumn === TEXT.CATEGORY ? true : false}
          placeholder={
            currentColumn === TEXT.TRANSACTION_APPROVAL_STATUS ||
            currentColumn === TEXT.ACCOUNT_STATUS ||
            currentColumn === TEXT.TRANSACTION_DISTRIBUTION_STATUS
              ? TEXT?.SELECT_STATUS
              : currentColumn === TEXT.OWNER_ACCOUNT_TYPE ||
                  currentColumn === TEXT.PAYMENT_METHOD
                ? TEXT.SELECT_TYPE
                : currentColumn === TEXT.CATEGORY
                  ? TEXT.SELECT_CATEGORY
                  : ""
          }
          searchPlaceholder={TEXT?.SEARCH_CATEGORY}
          statusPop
          dropdownType={
            currentColumn === TEXT.PAYMENT_METHOD ? "PaymentMethod" : ""
          }
          disabled={props?.activeTab !== currentIndex}
        />
      </div>
    );
  };

  useEffect(() => {
    const { activeTab, filterMapping, setFilterMapping, setActiveTab } = props;

    const NO_INPUT_REQUIRED_FIELDS = [
      TEXT.CONFIDENTIAL_TRANSACTION,
      TEXT.CREDIT_DEBIT,
    ];

    if (activeTab !== null) {
      const currentFilter = filterMapping[activeTab];

      if (currentFilter && currentFilter.active) {
        const hasColumn =
          currentFilter.column_name &&
          currentFilter.column_name.length >
            GlobalConfiguration?.lengthCheckZero;

        const hasCriteria =
          currentFilter.criteria_name &&
          currentFilter.criteria_name.length >
            GlobalConfiguration?.lengthCheckZero;

        const isArrayInput = Array.isArray(currentFilter.input_value);

        const hasInput =
          !isArrayInput &&
          currentFilter.input_value !== "" &&
          currentFilter.input_value !== null &&
          currentFilter.input_value !== undefined;

        const isNoInputRequired = NO_INPUT_REQUIRED_FIELDS.includes(
          currentFilter.column_name,
        );

        if (
          !isUserTyping &&
          hasColumn &&
          hasCriteria &&
          (hasInput || isNoInputRequired)
        ) {
          setTimeout(() => {
            const updatedData = filterMapping.map((itm, idx) => {
              if (idx === activeTab) {
                return {
                  ...itm,
                  active: false,
                };
              }

              return itm;
            });

            setFilterMapping(updatedData);
            setColumnState("");
            setActiveTab(null);
          }, 1000);
        }
      }
    }
  }, [
    props.filterMapping,
    props.activeTab,
    isUserTyping,
    props.setFilterMapping,
    props.setActiveTab,
  ]);

  useEffect(() => {
    const hasLabelsSelected = props?.filterMapping?.some(
      (filter) => filter?.column_name === TEXT.LABELS,
    );

    if (
      hasLabelsSelected &&
      props?.labelsOptions.length === GlobalConfiguration?.lengthCheckZero
    ) {
      fetchLabelsData();
    }
  }, [props?.filterMapping, props?.selectedOption, props?.labelsOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterCardRef.current &&
        !filterCardRef.current.contains(event.target)
      ) {
        if (
          event.target.closest('[class*="dropdown"]') ||
          event.target.closest('[class*="popover"]') ||
          event.target.closest('[class*="calendar"]')
        ) {
          return;
        }

        if (props?.activeTab !== null) {
          const currentFilter = props?.filterMapping[props?.activeTab];

          const isEmailValid =
            (currentFilter?.column_name === TEXT.ADDITIONAL_RECIPIENT ||
              currentFilter?.column_name ===
                TEXT.BENEFICIARY_ACCOUNT_EMAIL_ID) &&
            currentFilter?.input_value &&
            !isValidEmail(currentFilter.input_value);

          const isPanValid =
            currentFilter?.column_name === TEXT.BENEFICIARY_ACCOUNT_PAN &&
            currentFilter?.input_value &&
            !isValidPanNumber(currentFilter.input_value);

          const isIfscValid =
            (currentFilter?.column_name === TEXT.BENEFICIARY_ACCOUNT_IFSC ||
              currentFilter?.column_name === TEXT.OWNER_ACCOUNT_IFSC) &&
            currentFilter?.input_value &&
            !isValidIFSCCode(currentFilter.input_value);

          const isAccNoValid =
            (currentFilter?.column_name === TEXT.BENEFICIARY_ACCOUNT_NUMBER ||
              currentFilter?.column_name === TEXT.OWNER_ACCOUNT_NUMBER ||
              currentFilter?.column_name === TEXT.ACCOUNT_NUMBER) &&
            currentFilter?.input_value &&
            !isValidAccountNumber(currentFilter.input_value);

          const isMin3CharValid =
            !NOTE_DETAILS_EXCLUDED.includes(currentFilter?.column_name) &&
            currentFilter?.column_name !== TEXT.ADDITIONAL_RECIPIENT &&
            currentFilter?.column_name !== TEXT.BENEFICIARY_ACCOUNT_EMAIL_ID &&
            currentFilter?.column_name !== TEXT.BENEFICIARY_ACCOUNT_PAN &&
            currentFilter?.column_name !== TEXT.BENEFICIARY_ACCOUNT_IFSC &&
            currentFilter?.column_name !== TEXT.OWNER_ACCOUNT_IFSC &&
            currentFilter?.column_name !== TEXT.BENEFICIARY_ACCOUNT_NUMBER &&
            currentFilter?.column_name !== TEXT.OWNER_ACCOUNT_NUMBER &&
            currentFilter?.column_name !== TEXT.ACCOUNT_NUMBER &&
            currentFilter?.column_name !== TEXT.LABELS &&
            currentFilter?.input_value &&
            !isValidMinLength(currentFilter.input_value);

          if (
            isEmailValid ||
            isPanValid ||
            isIfscValid ||
            isAccNoValid ||
            isMin3CharValid
          ) {
            return;
          }

          setIsUserTyping(false);
          const isValid =
            currentFilter?.column_name &&
            currentFilter?.criteria_name &&
            currentFilter?.input_value !== "" &&
            currentFilter?.input_value !== null &&
            currentFilter?.input_value !== undefined;

          if (isValid) {
            const updatedData = props?.filterMapping.map((itm, idx) => {
              if (idx === props?.activeTab) {
                return {
                  ...itm,
                  active: false,
                };
              }
              return itm;
            });

            props?.setFilterMapping(updatedData);
            props?.setActiveTab(null);
            setColumnState("");
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props?.activeTab, props?.filterMapping]);

  return (
    <>
      <div className={`Mis_report_Main_div px-padding-md lg:px-padding-base`}>
        <div className="Mis_report_filter_SelectAcc_div">
          <TopBarButtons
            itemsDisabled={
              !(
                props?.filterMapping.every((itm) => itm.active === false) &&
                !isUserTyping &&
                selectedColumns &&
                selectedColumns.length > GlobalConfiguration?.lengthCheckZero
              )
            }
            inActiveVariant={true}
            items={[
              {
                imgColor: "var(--text-primary)",
                icon: ICONS?.ADD_BUTTON_WHITE,
                label: TEXT?.ADD_FILTER,
                onClick: () => {
                  if (
                    props?.filterMapping.every((itm) => {
                      return itm.active === false;
                    }) &&
                    selectedColumns &&
                    selectedColumns.length >
                      GlobalConfiguration?.lengthCheckZero
                  ) {
                    props?.setFilterMapping((prev) => {
                      return [
                        ...prev,
                        {
                          active: true,
                          column_name: "",
                          criteria_name: "",
                          input_value: "",
                        },
                      ];
                    });
                    props?.setActiveTab(props?.filterMapping.length);
                  }
                },
              },
            ]}
          />
          {props?.filterMapping?.length >
            GlobalConfiguration?.lengthCheckZero && (
            <>
              {props?.filterMapping.map((filter, index) => {
                return (
                  <div
                    ref={props?.activeTab === index ? filterCardRef : null}
                    style={{
                      backgroundColor: "var(--primary-card)",
                    }}
                    className="Mis_report_filter_name relative"
                  >
                    <div className="flex justify-between items-center">
                      <TitleSubtitlePair
                        title={
                          <>
                            Filter By{" "}
                            <span className="font-normal">(Optional)</span>
                          </>
                        }
                        variant="md"
                        titleColor="text-primary"
                        isBold={true}
                      />

                      <div
                        className={`${
                          (props?.filterMapping.every((itm) => {
                            return itm.active === false;
                          }) &&
                            selectedColumns &&
                            selectedColumns.length >
                              GlobalConfiguration?.lengthCheckZero) ||
                          props?.activeTab === index
                            ? "cursor-pointer"
                            : "cursor-not-allowed opacity-light-opacity"
                        } rounded-bl-border-radius-md rounded-tr-border-radius-md flex`}
                      >
                        <IconRoundButton
                          handleFunction={() => {
                            if (
                              (isLabelsLoading && props?.activeTab === index) ||
                              !(
                                (props?.filterMapping.every(
                                  (itm) => itm.active === false,
                                ) &&
                                  selectedColumns &&
                                  selectedColumns.length >
                                    GlobalConfiguration?.lengthCheckZero) ||
                                props?.activeTab === index
                              )
                            )
                              return;

                            setIsUserTyping(false);
                            setColumnState("");
                            let data = props?.filterMapping.filter(
                              (item, idx) => {
                                return idx !== index;
                              },
                            );
                            props?.setFilterMapping(data);

                            if (props?.activeTab === index) {
                              props?.setActiveTab(null);
                            } else if (props?.activeTab > index) {
                              props?.setActiveTab(props?.activeTab - 1);
                            }
                          }}
                          isDisabled={
                            (isLabelsLoading && props?.activeTab === index) ||
                            !(
                              (props?.filterMapping.every(
                                (itm) => itm.active === false,
                              ) &&
                                selectedColumns &&
                                selectedColumns.length >
                                  GlobalConfiguration?.lengthCheckZero) ||
                              props?.activeTab === index
                            )
                          }
                          isLoading={false}
                          enabledBgColor={"var(--primary-nav)"}
                          icon={ICONS?.DELETE_WHITE_FILLED}
                          hoverOption="white"
                          title={TEXT.DELETE}
                        />
                      </div>
                    </div>

                    <>
                      <div className="Mis_report_filter_main">
                        {isLabelsLoading && props?.activeTab === index ? (
                          <>
                            <div className="w-full flex flex-col items-center justify-center py-padding-sm">
                              <Loader
                                width="var(--icon-lg)"
                                height="var(--icon-lg)"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="w-dropdown-width flex flex-col justify-center items-start gap-y-gap-xs h-fit">
                              <p className="text-font-xs  text-text-secondary text-left">
                                {TEXT.COLUMN}
                              </p>
                              <SingleSelectDropDownComponent
                                options={selectedColumns || []}
                                selected={
                                  props?.filterMapping[index]?.column_name
                                }
                                setSelected={(val) => {
                                  setColumnState(val);

                                  let data = props?.filterMapping.map(
                                    (itm, idx) => {
                                      if (idx === props?.activeTab) {
                                        return {
                                          ...itm,
                                          column_name: val,
                                          criteria_name: "",
                                          input_value: "",
                                        };
                                      }

                                      return itm;
                                    },
                                  );

                                  props?.setFilterMapping(data);
                                }}
                                type="small"
                                showSearch={true}
                                placeholder="Select column"
                                searchPlaceholder="Search column"
                                disabled={props?.activeTab !== index}
                              />
                            </div>
                            <div className="w-dropdown-width flex flex-col justify-center items-start gap-y-gap-xs h-fit">
                              <p className="text-font-xs  text-text-secondary text-left">
                                Criterion
                              </p>
                              <div className="w-full">
                                <SingleSelectDropDownComponent
                                  options={
                                    columnstate === TEXT.AMOUNT ||
                                    columnstate === TEXT.OPENING_BALANCE ||
                                    columnstate === TEXT.CLOSING_BALANCE ||
                                    props?.filterMapping[index]?.column_name ===
                                      TEXT.AMOUNT ||
                                    props?.filterMapping[index]?.column_name ===
                                      TEXT.OPENING_BALANCE ||
                                    props?.filterMapping[index]?.column_name ===
                                      TEXT.CLOSING_BALANCE
                                      ? [TEXT.MINIMUM, TEXT.MAXIMUM]
                                      : columnstate ===
                                            TEXT.ACCOUNT_STATEMENT_ENTRY_DATE ||
                                          props?.filterMapping[index]
                                            ?.column_name ===
                                            TEXT.ACCOUNT_STATEMENT_ENTRY_DATE
                                        ? ["Include"]
                                        : props?.selectedOption === TEXT.USER &&
                                            columnstate !== TEXT.USERNAME
                                          ? [
                                              TEXT.MINIMUM,
                                              TEXT.MAXIMUM,
                                              "Include",
                                              "Exclude",
                                            ]
                                          : SIMILAR_COLUMNS.includes(
                                                props?.filterMapping[index]
                                                  ?.column_name,
                                              )
                                            ? ["Include", "Exclude", "Similar"]
                                            : props?.filterMapping[index]
                                                  ?.column_name ===
                                                TEXT.CREDIT_DEBIT
                                              ? [
                                                  "Show only credit",
                                                  "Show only debit",
                                                ]
                                              : ["Include", "Exclude"]
                                  }
                                  selected={
                                    props?.filterMapping[index]?.criteria_name
                                  }
                                  setSelected={(val) => {
                                    let data = props?.filterMapping.map(
                                      (itm, idx) => {
                                        if (idx === props?.activeTab) {
                                          return {
                                            ...itm,
                                            criteria_name: val,
                                          };
                                        } else {
                                          return itm;
                                        }
                                      },
                                    );
                                    props?.setFilterMapping(data);
                                  }}
                                  type="small"
                                  showSearch={false}
                                  placeholder={TEXT?.SELECT_CRITERION}
                                  searchPlaceholder=""
                                  disabled={
                                    props?.activeTab !== index ||
                                    !props?.filterMapping[index]?.column_name
                                  }
                                />
                              </div>
                            </div>

                            <div
                              style={{
                                display:
                                  filter.column_name &&
                                  filter.column_name !== "" &&
                                  filter.column_name !==
                                    TEXT.CONFIDENTIAL_TRANSACTION &&
                                  filter.column_name !== TEXT.CREDIT_DEBIT
                                    ? "flex"
                                    : "none",
                              }}
                              className="flex flex-grow-1 flex-col justify-end gap-y-gap-xs w-full Qmd:w-dropdown-width"
                            >
                              {renderField(filter, index)}
                            </div>
                          </>
                        )}
                      </div>
                      <div
                        onMouseDown={(e) => e.stopPropagation()}
                        className={` w-full flex flex-row justify-end  gap-x-gap-md `}
                      >
                        <PrimaryButton
                          enabledBgColor=""
                          btnText={TEXT.CLEAR}
                          outLineColor="var(--text-primary)"
                          enabledTextColor="var(--text-primary)"
                          handleFunction={() => {
                            const disabled =
                              isLabelsLoading ||
                              !props?.filterMapping[index]?.column_name ||
                              (props?.activeTab !== index &&
                                !(
                                  props?.filterMapping.every(
                                    (itm) => itm.active === false,
                                  ) &&
                                  selectedColumns &&
                                  selectedColumns.length >
                                    GlobalConfiguration?.lengthCheckZero
                                ));

                            if (disabled) return;
                            setColumnState(
                              props?.filterMapping[index].column_name,
                            );
                            let data = props?.filterMapping.map((itm, idx) => {
                              if (idx === index) {
                                return {
                                  column_name: "",
                                  criteria_name: "",
                                  input_value: "",
                                  active: true,
                                };
                              } else {
                                return itm;
                              }
                            });
                            props?.setActiveTab(index);
                            props?.setFilterMapping(data);
                            setColumnState("");
                          }}
                          hoverOption="white"
                          isDisabled={
                            isLabelsLoading ||
                            !props?.filterMapping[index]?.column_name ||
                            (props?.activeTab !== index &&
                              !(
                                props?.filterMapping.every((itm) => {
                                  return itm.active === false;
                                }) &&
                                selectedColumns &&
                                selectedColumns.length >
                                  GlobalConfiguration?.lengthCheckZero
                              ))
                          }
                        />
                      </div>
                    </>
                  </div>
                );
              })}
            </>
          )}
        </div>
        <div className="h-pagination-height mb-margin-lg"></div>
      </div>
    </>
  );
};

export default MISReportCreateFilter;
